/**
 * BillingConfigPage.tsx — Billing-as-Code editor (Stripe Facade Refactor, Phase 8)
 *
 * Declare your product catalog as code (YAML or JSON) and apply it idempotently
 * to Stripe. "Preview" shows the exact diff before anything changes; "Apply"
 * commits it. Re-applying the same config is a safe no-op.
 *
 * @module packages/billing-admin/src/pages/BillingConfigPage
 * @since 2026-06-21
 */
import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';

interface ConfigChange {
  resource: 'product' | 'price';
  action: 'create' | 'update' | 'archive' | 'noop';
  key: string;
  detail: string;
  stripeId?: string;
}

interface ConfigSyncResult {
  configured: boolean;
  applied: boolean;
  changes: ConfigChange[];
  errors: string[];
}

const STARTER_YAML = `version: 1
products:
  - key: pro
    name: Pro Plan
    description: For growing teams
    prices:
      - lookupKey: pro_monthly
        currency: usd
        unitAmountCents: 4900
        recurringInterval: month
`;

const ACTION_STYLES: Record<ConfigChange['action'], string> = {
  create: 'bg-emerald-100 text-emerald-800',
  update: 'bg-amber-100 text-amber-800',
  archive: 'bg-rose-100 text-rose-700',
  noop: 'bg-slate-100 text-slate-500',
};

export default function BillingConfigPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const [source, setSource] = useState(STARTER_YAML);
  const [result, setResult] = useState<ConfigSyncResult | null>(null);
  const [busy, setBusy] = useState<false | 'preview' | 'apply'>(false);
  const [error, setError] = useState<string | null>(null);

  async function run(mode: 'preview' | 'apply') {
    if (!activeOrg?.orgId) return;
    setBusy(mode);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(
        `${apiBaseUrl}/api/v1/orgs/${activeOrg.orgId}/billing-config/${mode}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ config: source }),
        },
      );
      const body = (await res.json()) as ConfigSyncResult & { error?: string; detail?: string };
      if (!res.ok) {
        setError(body.detail ?? body.error ?? `Request failed (${res.status})`);
        return;
      }
      setResult(body);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminLayout title="Billing as Code">
      <p className="mb-6 text-sm text-slate-500">
        Declare your product catalog as code. Preview the exact diff, then apply it to Stripe.
        Re-applying the same config is a safe no-op.
      </p>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex flex-col">
          <label className="mb-2 text-sm font-medium text-slate-700" htmlFor="config-source">
            Config (YAML or JSON)
          </label>
          <textarea
            id="config-source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            spellCheck={false}
            className="h-96 w-full rounded-lg border border-slate-300 bg-slate-50 p-3 font-mono text-sm text-slate-800 focus:border-indigo-500 focus:outline-none"
          />
          <div className="mt-3 flex gap-3">
            <button
              type="button"
              onClick={() => void run('preview')}
              disabled={busy !== false}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {busy === 'preview' ? 'Previewing…' : 'Preview diff'}
            </button>
            <button
              type="button"
              onClick={() => void run('apply')}
              disabled={busy !== false}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {busy === 'apply' ? 'Applying…' : 'Apply to Stripe'}
            </button>
          </div>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium text-slate-700">
            {result?.applied ? 'Applied changes' : 'Planned changes'}
          </h3>

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {error}
            </div>
          )}

          {result && !result.configured && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Stripe is not connected for this organization. Connect Stripe to apply config.
            </div>
          )}

          {result && result.configured && result.changes.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
              No changes — your catalog already matches this config.
            </div>
          )}

          {result && result.configured && result.changes.length > 0 && (
            <ul className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white">
              {result.changes.map((change, i) => (
                <li key={`${change.resource}-${change.key}-${i}`} className="flex items-start gap-3 px-4 py-3">
                  <span className={`mt-0.5 inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium uppercase ${ACTION_STYLES[change.action]}`}>
                    {change.action}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800">
                      {change.resource} · {change.key}
                    </p>
                    <p className="text-xs text-slate-500">{change.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {result && result.errors.length > 0 && (
            <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              <p className="font-medium">Errors</p>
              <ul className="mt-1 list-inside list-disc">
                {result.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
